<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { useBoard } from "../stores/board.js";
import { useViewport } from "../stores/viewport.js";
import { DEFAULT_REPO_FOLDER } from "@shared/repoFolders.js";
import { loadRepos, repoFolders, repos, reposError, reposLoading } from "../lib/repos.js";
import { formatHours } from "../lib/format.js";
import { copyText } from "../lib/clipboard.js";
import { dominantValue } from "@shared/rollup.js";
import { taskLink } from "../lib/taskRoute.js";
import { vSynced } from "../lib/syncedValue.js";
import { vCollab } from "../lib/collabValue.js";
import { HOUR_MS, URGENCY_MAX, URGENCY_MIN, branchUrl, loggedHours, urgencyLabel, type EditableField, type Status, type Task, type TaskTime } from "@shared/types.js";
import LockTag from "./LockTag.vue";
import RemoteCarets from "./RemoteCarets.vue";
import TimelineEntry from "./TimelineEntry.vue";
import StatusChip from "./StatusChip.vue";
import { toast } from "../lib/toast.js";
import MoveToTeamModal from "./MoveToTeamModal.vue";
import PinButton from "./PinButton.vue";

const board = useBoard();
const viewport = useViewport();
/**
 * The open task, held past its deselection. `board.selected` goes null the
 * moment the drawer closes, but the drawer unmounts a render later, and a field
 * losing focus in that gap still fires `change` — the browser's own, after the
 * back-button save already dispatched one. Keeping the last task means that
 * late handler compares against the right row (and writes nothing) instead of
 * reading `.title` off null. A deleted task is already gone from the store, so
 * patchTask drops any write to it.
 */
const task = computed<Task>((prev) => board.selected ?? prev!);
const draft = ref("");

/**
 * The task is still being written and nothing about it has been sent (see
 * DRAFT_ID in the board store). The drawer is where it gets its name, which is
 * what saves it, so everything that needs a task the server knows about —
 * a link to it, logged hours, comments, Claude, a sub-task, moving it — waits.
 */
const unsaved = computed(() => board.isDraft(task.value.id));

const allEntries = computed(() => board.timelines.get(task.value.id) ?? []);
const entries = computed(() =>
  viewport.commentsOnly ? allEntries.value.filter((e) => e.kind === "comment") : allEntries.value,
);
/** How many system events the filter is currently hiding, so the checkbox can
 *  say what it is costing rather than silently swallowing the history. */
const hiddenCount = computed(() => allEntries.value.length - entries.value.length);

// ── field locks ──────────────────────────────────────────────────────────
/** Someone else typing in this field of this task, or null. */
const holder = (field: EditableField) => board.lockHolder(task.value.id, field);
/** Everyone else typing in a live text field (title, notes) — any number of them. */
const editors = (field: EditableField) => board.editorsOf(task.value.id, field);
/** Other people's carets in a live text field, for RemoteCarets. */
const sels = (field: "title" | "notes") => board.textSels.get(`${task.value.id}:${field}`);
const notesInput = ref<HTMLTextAreaElement | null>(null);

/**
 * Set while the drawer focuses the title itself on open. That focus is the
 * drawer being helpful, not a person choosing to edit — claiming on it would
 * lock the title for everyone merely because someone looked at the task.
 */
let quietFocus = false;

/** Attributes for a lockable input: disabled and tinted with the holder's
 *  colour while they are in it, and claimed for us when we click in or type. */
function lockable(field: EditableField): Record<string, unknown> {
  const user = holder(field);
  return {
    disabled: user !== null,
    class: { locked: user !== null },
    style: user ? { "--lock-color": user.color } : undefined,
    title: user ? `${user.displayName} is editing this` : undefined,
    onFocus: () => {
      if (!quietFocus) board.claim(task.value.id, field);
    },
    // Typing into the auto-focused title is choosing to edit it.
    onInput: () => board.claim(task.value.id, field),
    onBlur: () => board.release(task.value.id, field),
  };
}

async function setField(key: "title" | "repo" | "branch" | "notes", value: string): Promise<void> {
  if (value === (task.value[key] ?? "")) return;
  await board.patchTask(task.value.id, { [key]: value || null });
}

async function setEst(value: string): Promise<void> {
  const n = value === "" ? null : Number(value);
  const estHours = Number.isFinite(n) ? n : null;
  if (estHours === task.value.estHours) return;
  await board.patchTask(task.value.id, { estHours });
}

// ── logged hours ─────────────────────────────────────────────────────────
// Human time only, entered after the fact. Claude's is read off its tmux
// session, so there is no clock to start here.
/** Everyone's time on this task. Defensive default: a task from a server that
 *  predates per-user time has no `time` key at all, and `.find` on undefined
 *  throws where a missing row should just read as "nothing logged". */
const allTime = computed((): TaskTime[] => task.value.time ?? []);

const myHours = computed(() => allTime.value.find((t) => t.userId === board.me?.id)?.hours ?? 0);

/** Everyone else who has logged time here. */
const others = computed(() => allTime.value.filter((t) => t.userId !== board.me?.id && t.hours > 0));

const spentHours = computed(() => loggedHours(allTime.value));

/** What is typed in the box. `v-model` on a number input hands over a number
 *  once there is one, and the string back while the box is empty. */
const logDraft = ref<string | number>("");
const logging = ref(false);
const logAmount = computed(() => {
  const text = String(logDraft.value).trim();
  const n = Number(text);
  return text !== "" && Number.isFinite(n) && n !== 0 ? n : null;
});
// A different task in the same drawer starts with an empty box.
watch(() => task.value.id, () => (logDraft.value = ""));

async function logHours(): Promise<void> {
  const hours = logAmount.value;
  if (hours === null || logging.value) return;
  logging.value = true;
  try {
    await board.logHours(task.value.id, hours);
    logDraft.value = "";
  } finally {
    logging.value = false;
  }
}

// ── progress ─────────────────────────────────────────────────────────────
/** A parent's progress is rolled up from its children and is not editable
 *  here — storing it would be the cached-column mistake CLAUDE.md warns about. */
const isLeaf = computed(() => !board.kids.get(task.value.id)?.length);
/** Null for a top-level task; the sub-tasks section shows a way back up otherwise. */
const parent = computed(() => (task.value.parentId ? (board.tasks.get(task.value.parentId) ?? null) : null));
/** Direct children only, in tree order. Deeper levels are one click away: each
 *  row opens that child, whose own drawer lists its children in turn. */
const children = computed(() => board.kids.get(task.value.id) ?? []);
const childrenDone = computed(() => children.value.filter((c) => c.status === "done").length);
const shownChildren = computed(() =>
  viewport.hideDoneSubtasks ? children.value.filter((c) => c.status !== "done") : children.value,
);
const childCount = (id: string): number => board.kids.get(id)?.length ?? 0;
const childPct = (id: string): number => Math.round((board.rollups.get(id)?.percent ?? 0) * 100);
const rollupPercent = computed(() => board.rollups.get(task.value.id)?.percent ?? 0);
/** A parent's estimate is the sum of its leaves', exactly as its progress is
 *  the weighted sum of theirs. Its own est_hours column is dead data: the
 *  rollup only reads a leaf's, so an editable box there would take a number and
 *  then visibly ignore it. */
const rollupEstHours = computed(() => board.rollups.get(task.value.id)?.estHours ?? null);
const progressPct = computed(() => Math.round(rollupPercent.value * 100));

/** Dragging previews in the store so the gantt bar tracks the thumb, over
 *  anything anyone else changes meanwhile; the server only hears about it on
 *  release. Same trick as dragging a bar. */
function dragProgress(value: string): void {
  board.previewTask(task.value.id, { progress: Number(value) / 100 });
}

async function commitProgress(value: string): Promise<void> {
  board.endPreview(task.value.id);
  await board.patchTask(task.value.id, { progress: Number(value) / 100 });
}

/** Back to "read it off the status", which is where every task starts. */
async function clearProgress(): Promise<void> {
  await board.patchTask(task.value.id, { progress: null });
}

// ── urgency ──────────────────────────────────────────────────────────────
/** Settable on any task, parent or leaf: unlike progress, a parent's own
 *  urgency means something — "this whole feature is pressing". */
const urgencyStep = computed(() => Math.max(URGENCY_MIN, task.value.urgency ?? URGENCY_MIN));
const urgencyText = computed(() => urgencyLabel(urgencyStep.value));
/** What the tree sorts this task by, when something underneath outranks it. */
const liftedUrgency = computed(() => {
  const rolled = board.rollups.get(task.value.id)?.urgency ?? 0;
  return rolled > urgencyStep.value ? urgencyLabel(rolled) : null;
});

/** Like progress: the store follows the thumb, the server hears on release. */
function dragUrgency(value: string): void {
  board.previewTask(task.value.id, { urgency: Number(value) });
}

async function commitUrgency(value: string): Promise<void> {
  board.endPreview(task.value.id);
  await board.patchTask(task.value.id, { urgency: Number(value) });
}

// ── repo / branch ────────────────────────────────────────────────────────
/** Whether this task's team codes. If not, there is no repo, branch or Claude run to offer. */
const coding = computed(() => board.isCoding(task.value));
/** What an empty box falls back to, shown as its placeholder so it is clear a
 *  sub-task is already in a repo before anyone types one. */
const inheritedRepo = computed(() => board.inherited(task.value, "repo"));
const inheritedBranch = computed(() => board.inherited(task.value, "branch"));
const repo = computed(() => task.value.repo || inheritedRepo.value);
const branch = computed(() => task.value.branch || inheritedBranch.value);
const link = computed(() => branchUrl(repo.value, branch.value));

// ── repo picker ──────────────────────────────────────────────────────────
/** The dropdown's choices: the server's repos, plus the task's own when the
 *  list lacks it (an old row, another folder's), so the select can show it. */
const repoChoices = computed(() => {
  const own = task.value.repo;
  return own && !repos.value.includes(own) ? [own, ...repos.value] : repos.value;
});

/**
 * How the bare repos on the box are addressed over ssh.
 *
 * The folder is not decoration: an scp-style path is relative to the git user's
 * home, and the repos live in ~git/repos — or a code team's own folder beside it
 * (shared/repoFolders.ts) — which is why this project's own origin is
 * `gitvps:repos/todos.git`. Without it the command clones nothing.
 */
const CLONE_HOST = "gitvps";

const cloneCommand = computed(() => {
  const name = (repo.value ?? "").trim().replace(/\.git$/, "");
  return name ? `git clone ${CLONE_HOST}:${repoFolders.value[name] ?? DEFAULT_REPO_FOLDER}/${name}.git` : "";
});
// The dropdown's list, and which folder each repo is in; cached per team, so this is one read a board.
watch(
  () => (coding.value ? (task.value.teamId ?? null) : undefined),
  (team) => {
    if (team !== undefined) void loadRepos(team);
  },
  { immediate: true },
);

/** Flips to a tick for a moment, so a silent clipboard write is visible. */
const copied = ref(false);
let copiedTimer: number | null = null;

async function copyClone(): Promise<void> {
  const text = cloneCommand.value;
  if (!text) return;
  await copyText(text);
  copied.value = true;
  if (copiedTimer !== null) window.clearTimeout(copiedTimer);
  copiedTimer = window.setTimeout(() => (copied.value = false), 1200);
}

const linkCopied = ref(false);
let linkTimer: number | null = null;

async function copyLink(): Promise<void> {
  await copyText(taskLink(task.value.id));
  linkCopied.value = true;
  if (linkTimer !== null) window.clearTimeout(linkTimer);
  linkTimer = window.setTimeout(() => (linkCopied.value = false), 1200);
}

const titleCopied = ref(false);
let titleTimer: number | null = null;

async function copyTitle(): Promise<void> {
  await copyText(task.value.title);
  titleCopied.value = true;
  if (titleTimer !== null) window.clearTimeout(titleTimer);
  titleTimer = window.setTimeout(() => (titleCopied.value = false), 1200);
}

onBeforeUnmount(() => {
  if (copiedTimer !== null) window.clearTimeout(copiedTimer);
  if (linkTimer !== null) window.clearTimeout(linkTimer);
  if (titleTimer !== null) window.clearTimeout(titleTimer);
  disarmDelete();
});

/**
 * Who the "who" row offers: the task's team. Nobody else can see a personal
 * task, so it can only be its owner's. Someone already on it stays listed
 * even after leaving the team, so they can still be taken off.
 */
const assignable = computed(() => {
  const t = task.value;
  if (t.privateTo) return [...board.users.values()].filter((u) => u.id === t.privateTo);
  const members = new Set(board.teams.find((team) => team.id === t.teamId)?.memberIds ?? []);
  return [...board.users.values()].filter((u) => members.has(u.id) || t.assignees.includes(u.id));
});

async function toggleAssignee(id: string): Promise<void> {
  const current = task.value.assignees;
  const next = current.includes(id) ? current.filter((u) => u !== id) : [...current, id];
  await board.patchTask(task.value.id, { assignees: next });
}

async function send(): Promise<void> {
  const body = draft.value.trim();
  if (!body) return;
  draft.value = "";
  await board.comment(task.value.id, body);
}

/** Move the drawer onto a new child — same as "+ Task", which selects what it
 *  made. The parent's row is left folded or open, as it was. */
function addChild(): void {
  const parentId = task.value.id;
  const child = board.addTask({ parentId });
  board.select(child.id);
}

/** A new task under the same parent, placed directly after this one rather
 *  than at the end of the list, then opened like "+ Child" does. */
function addSibling(): void {
  // A top-level sibling has no parent to inherit from, so it copies what the
  // parent would have given it: personal, or this task's team (the tmux view
  // shows every board's), and the repo — its own, else the one its tree is
  // filed under — or it lands under "no repo", nowhere near this one.
  const t = task.value;
  const root = t.parentId === null;
  const sibling = board.addTask({
    parentId: t.parentId,
    afterId: t.id,
    ...(t.privateTo ? { personal: true } : root ? { teamId: t.teamId } : {}),
    ...(root ? { repo: t.repo ?? dominantValue(t, board.kids, (x) => x.repo) } : {}),
  });
  board.select(sibling.id);
}

// ── delete: arm, then confirm ─────────────────────────────────────────────
/**
 * Two clicks on the same button instead of a confirm() dialog: the first arms
 * it (red, "Delete?"), the second deletes. A native dialog blocks the whole
 * page and on a phone is easy to tap through without reading. Arming wears
 * off on its own, so a stray first click days later cannot be the half of a
 * delete nobody meant.
 */
const DISARM_MS = 3000;
const deleteArmed = ref(false);
let disarmTimer: number | null = null;

function disarmDelete(): void {
  deleteArmed.value = false;
  if (disarmTimer !== null) window.clearTimeout(disarmTimer);
  disarmTimer = null;
}

/** Everything under this task goes with it; the armed tooltip says how much. */
const descendantCount = computed(() => board.rollups.get(task.value.id)?.descendantCount ?? 0);

/** Take this shared task, and everything under it, into the reader's personal tasks. */
async function makePersonal(): Promise<void> {
  const t = task.value;
  const under = board.kids.get(t.id)?.length ? " and everything under it" : "";
  if (!confirm(`Move “${t.title}”${under} into your personal tasks? Nobody else will see it.`)) return;
  try {
    await board.makePersonal(t.id);
  } catch (err) {
    toast(`Not moved: ${(err as Error).message}`, 6000);
  }
}

/** The way back: a personal task onto one of the reader's teams, picked in a popup. */
const moveTeamOpen = ref(false);

async function remove(): Promise<void> {
  // Nothing was ever saved, so there is nothing to confirm losing: one click
  // drops the draft and closes the drawer.
  if (unsaved.value) {
    await board.removeTask(task.value.id);
    return;
  }
  if (!deleteArmed.value) {
    deleteArmed.value = true;
    disarmTimer = window.setTimeout(disarmDelete, DISARM_MS);
    return;
  }
  const id = task.value.id;
  disarmDelete();
  await board.removeTask(id);
}

/**
 * Duration in hours for a task that carries dates.
 *
 * The drawer no longer offers a way to SET them — work here is sized in hours,
 * not scheduled against a calendar — but tasks dated before that still have
 * them, and they still drag on the chart, so the readout stays.
 */
const durationHours = computed(() => {
  const t = task.value;
  if (t.startMs === null || t.endMs === null) return null;
  return Math.round((t.endMs - t.startMs) / HOUR_MS);
});

const titleInput = ref<HTMLTextAreaElement | null>(null);

/** The title box wraps and grows, and Shift+Enter starts a new line, as the
 *  tmux bar's quick add does. A title is one line, though, so text of several
 *  is titled by its first and the rest goes into the notes — in one patch, so
 *  naming a draft carries both. */
async function commitTitle(el: HTMLTextAreaElement): Promise<void> {
  const lines = el.value.split("\n");
  const first = lines.findIndex((l) => l.trim());
  if (!unsaved.value) {
    // Live text: the title saves itself as it is typed (server/textSessions.ts).
    // Only the lines after the first have somewhere else to go.
    if (first === -1 || !lines.slice(first + 1).some((l) => l.trim())) return;
    const rest = lines.slice(first + 1).join("\n").trim();
    el.value = lines[first]!.trim();
    // Heard by the live text box as typing, so everyone's title loses the lines too.
    el.dispatchEvent(new Event("input"));
    const notes = task.value.notes ? `${task.value.notes}\n\n${rest}` : rest;
    await board.patchTask(task.value.id, { notes });
    return;
  }
  if (first === -1 || !lines.slice(first + 1).some((l) => l.trim())) {
    return setField("title", el.value.trim());
  }
  const title = lines[first]!.trim();
  const rest = lines.slice(first + 1).join("\n").trim();
  const notes = task.value.notes ? `${task.value.notes}\n\n${rest}` : rest;
  // Still focused, so v-synced would keep the lines it holds.
  el.value = title;
  await board.patchTask(task.value.id, { title, notes });
}

/** For a browser without `field-sizing: content`; the CSS caps the height. */
function fitTitle(): void {
  const el = titleInput.value;
  if (!el || CSS.supports("field-sizing", "content")) return;
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
}
// v-synced writes the value without an input event.
watch(() => [task.value.id, task.value.title], () => void nextTick(fitTitle), { flush: "post" });

function onTitleEnter(ev: KeyboardEvent): void {
  if (ev.shiftKey || ev.isComposing) return;
  ev.preventDefault();
  void commitTitle(ev.target as HTMLTextAreaElement);
}

watch(() => task.value.id, () => {
  draft.value = "";
  // Armed for the task being left, not for whichever one opens next.
  disarmDelete();
});

/** Ready to type on open, and again on switching tasks: the drawer stays
 *  mounted between selections, so onMounted alone would only fire once. Post
 *  flush, so the input exists on the first open.
 *
 *  Never out of a field somebody is in: saving a draft swaps its local id for
 *  the server's, which is a "new task" here, and it happens the moment the
 *  title is committed — with the cursor already in the next field. */
watch(
  () => task.value.id,
  () => {
    const active = document.activeElement;
    if (active instanceof HTMLElement && active.closest(".drawer")) return;
    quietFocus = true;
    titleInput.value?.focus();
    quietFocus = false;
  },
  { immediate: true, flush: "post" },
);
</script>

<template>
  <aside class="drawer">
    <div class="drawer-head">
      <div style="display: flex; gap: 6px; align-items: center">
        <StatusChip
          :status="task.status"
          @change="(s: Status) => board.setStatus(task.id, s)"
        />
        <span style="flex: 1"></span>
        <!-- Not a draft: until it is named there is nothing to come back to. -->
        <PinButton v-if="!unsaved" :pin="{ kind: 'task', id: task.id }" name="this task" />
        <button v-if="!unsaved" class="btn ghost" title="Copy a link to this task" @click="copyLink">
          <span class="wide-only">{{ linkCopied ? "Copied" : "Copy link" }}</span>
          <span class="narrow-only" aria-label="Copy link">{{ linkCopied ? "✓" : "🔗" }}</span>
        </button>
        <button
          class="btn"
          :class="deleteArmed ? 'danger' : 'ghost'"
          :title="unsaved
            ? 'Throw this away — nothing has been saved'
            : deleteArmed
              ? `Click again to delete${descendantCount ? ` this and ${descendantCount} sub-task${descendantCount === 1 ? '' : 's'}` : ''}`
              : 'Delete (click twice)'"
          @click="remove"
        >{{ unsaved ? "Discard" : deleteArmed ? "Delete?" : "Delete" }}</button>
        <button class="btn ghost" title="Close" aria-label="Close" @click="board.select(null)">✓</button>
      </div>
      <div class="drawer-title-row">
        <div class="collab-wrap">
        <textarea
          ref="titleInput"
          v-collab="{ key: task.id, field: 'title', value: task.title, live: !unsaved, onRemote: fitTitle }"
          class="drawer-title"
          rows="1"
          v-bind="lockable('title')"
          :placeholder="unsaved ? 'Name it to add it' : undefined"
          aria-label="Title (Shift+Enter for a new line: the lines after the first go into the notes)"
          @input="fitTitle"
          @keydown.enter="onTitleEnter"
          @change="commitTitle($event.target as HTMLTextAreaElement)"
        ></textarea>
        <RemoteCarets :target="titleInput" :sels="sels('title')" />
        </div>
        <LockTag v-for="u in editors('title')" :key="u.id" :user="u" />
        <button
          class="picker-btn"
          type="button"
          :class="{ done: titleCopied }"
          title="Copy the title"
          aria-label="Copy the title"
          @click="copyTitle"
        >{{ titleCopied ? "✓" : "⧉" }}</button>
      </div>
      <!-- Nothing has been sent about this one yet, and nobody else can see
           it; say so, since the drawer is otherwise missing half its parts. -->
      <p v-if="unsaved" class="drawer-personal">
        Not saved yet — name it and it is added{{ task.privateTo ? ", where only you can see it" : "" }}.
      </p>
      <p v-else-if="task.privateTo" class="drawer-personal">
        <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true">
          <rect x="3" y="7" width="10" height="7" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.4" />
          <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" fill="none" stroke="currentColor" stroke-width="1.4" />
        </svg>
        Personal — only you can see this
        <template v-if="board.teams.length">
          ·
          <button class="linkish" type="button" title="Move it, and everything under it, onto a team's board" @click="moveTeamOpen = true">
            Move to a team
          </button>
        </template>
      </p>
      <p v-else class="drawer-personal">
        <button class="linkish" type="button" title="Move it, and everything under it, where only you can see it" @click="makePersonal">
          <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true">
            <rect x="3" y="7" width="10" height="7" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.4" />
            <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" fill="none" stroke="currentColor" stroke-width="1.4" />
          </svg>
          Make personal
        </button>
      </p>
    </div>

    <div class="fields">
      <!-- Repo and branch only where the team codes (see board.isCoding). -->
      <template v-if="coding">
        <label>repo <LockTag :user="holder('repo')" /></label>
        <div class="picker">
          <select
            v-bind="lockable('repo')"
            aria-label="Repo"
            @change="setField('repo', ($event.target as HTMLSelectElement).value)"
          >
            <option value="" :selected="!task.repo">{{ inheritedRepo ? `${inheritedRepo} (inherited)` : "no repo" }}</option>
            <option v-for="name in repoChoices" :key="name" :value="name" :selected="name === task.repo">{{ name }}</option>
            <option v-if="reposLoading && !repos.length" disabled>loading…</option>
            <option v-else-if="reposError" disabled>{{ reposError }}</option>
          </select>
          <button
            class="picker-btn"
            type="button"
            :class="{ done: copied }"
            :disabled="!cloneCommand"
            :title="cloneCommand ? `Copy: ${cloneCommand}` : 'Pick a repo first'"
            @click="copyClone"
          >{{ copied ? "✓" : "⧉" }}</button>
        </div>

        <label>branch <LockTag :user="holder('branch')" /></label>
        <input
          v-synced="{ key: task.id, value: task.branch }"
          v-bind="lockable('branch')"
          :placeholder="inheritedBranch ?? 'no branch'"
          @change="setField('branch', ($event.target as HTMLInputElement).value)"
        />

        <template v-if="link">
          <label></label>
          <a class="branch" :href="link" target="_blank" rel="noreferrer">
            open branch ↗
          </a>
        </template>

        <!-- Every undone task with a repo is on the Claude queue unless this is ticked. -->
        <template v-if="!unsaved">
          <label>claude</label>
          <label class="claude-hide" title="Every undone task with a repo waits in the tmux view's Claude queue; ticked, this one does not">
            <input
              type="checkbox"
              :checked="task.claudeHidden"
              @change="board.patchTask(task.id, { claudeHidden: ($event.target as HTMLInputElement).checked })"
            />
            hide from Claude
          </label>
          <label></label>
          <label class="claude-hide" title="Needs a person with root (sudo, /etc, a service). Never queued for Claude; a Claude asks for one with create_admin_task">
            <input
              type="checkbox"
              :checked="task.adminTask"
              @change="board.patchTask(task.id, { adminTask: ($event.target as HTMLInputElement).checked })"
            />
            admin task (needs root)
          </label>
          <label></label>
          <label class="claude-hide" title="Once its worktree is merged into staging, it goes straight to done instead of review">
            <input
              type="checkbox"
              :checked="task.skipReview"
              @change="board.patchTask(task.id, { skipReview: ($event.target as HTMLInputElement).checked })"
            />
            skip review
          </label>
        </template>
      </template>

      <label>progress <LockTag :user="holder('progress')" /></label>
      <div v-if="isLeaf" class="slider-row">
        <input
          v-synced="{ key: task.id, value: progressPct }"
          v-bind="lockable('progress')"
          type="range"
          min="0"
          max="100"
          step="5"
          @input="dragProgress(($event.target as HTMLInputElement).value)"
          @change="commitProgress(($event.target as HTMLInputElement).value)"
          @blur="board.endPreview(task.id)"
        />
        <span class="pct">{{ progressPct }}%</span>
        <button
          v-if="task.progress !== null"
          :disabled="holder('progress') !== null"
          class="btn ghost slider-reset"
          type="button"
          title="Back to following the status"
          @click="clearProgress"
        >auto</button>
      </div>
      <div v-else class="slider-row">
        <span class="mini"><i :style="{ width: progressPct + '%' }"></i></span>
        <span class="pct">{{ progressPct }}%</span>
        <span style="color: var(--faint); font-size: 11px">rolled up</span>
      </div>

      <label>urgency <LockTag :user="holder('urgency')" /></label>
      <div class="slider-row">
        <input
          v-synced="{ key: task.id, value: urgencyStep }"
          v-bind="lockable('urgency')"
          type="range"
          :min="URGENCY_MIN"
          :max="URGENCY_MAX"
          step="1"
          @input="dragUrgency(($event.target as HTMLInputElement).value)"
          @change="commitUrgency(($event.target as HTMLInputElement).value)"
          @blur="board.endPreview(task.id)"
        />
        <span class="urgency-label" :class="'u' + urgencyStep">{{ urgencyText }}</span>
        <span
          v-if="liftedUrgency"
          style="color: var(--faint); font-size: 11px"
          title="Sorted by the most urgent unfinished sub-task"
        >sub-task {{ liftedUrgency }}</span>
      </div>

      <label>estimate <LockTag :user="holder('estHours')" /></label>
      <input
        v-if="isLeaf"
        v-synced="{ key: task.id, value: task.estHours }"
        v-bind="lockable('estHours')"
        type="number" min="0" step="0.5" placeholder="hours"
        @change="setEst(($event.target as HTMLInputElement).value)"
      />
      <div v-else class="slider-row">
        <span>{{ rollupEstHours === null ? "—" : formatHours(rollupEstHours) }}</span>
        <span style="color: var(--faint); font-size: 11px">rolled up</span>
      </div>

      <!-- Hours are logged against a task the server knows about, and each
           one is narrated on its timeline, so none until it is saved. -->
      <label v-if="!unsaved">spent</label>
      <form v-if="!unsaved" class="slider-row" @submit.prevent="logHours">
        <span class="time-mine" title="Hours you have logged on this task">
          {{ myHours > 0 ? formatHours(myHours) : "—" }}
        </span>
        <input
          v-model="logDraft"
          type="number"
          step="0.25"
          placeholder="+ hours worked"
          aria-label="Hours to log"
          title="Hours you worked, added to yours. Negative takes some back."
          style="flex: 1"
        />
        <button class="btn primary" type="submit" :disabled="logAmount === null || logging">Log</button>
      </form>

      <!-- Everyone else's time. Hidden entirely when you are the only one who
           has worked on this, so a solo board never grows a second row. -->
      <template v-if="others.length">
        <label>others</label>
        <div class="time-others">
          <div v-for="t in others" :key="t.userId" class="time-row">
            <span class="avatar" :style="{ background: board.userColor(t.userId) }">
              {{ board.userName(t.userId).slice(0, 2) }}
            </span>
            <span class="time-who">{{ board.userName(t.userId) }}</span>
            <span class="time-hours">{{ formatHours(t.hours) }}</span>
          </div>
          <div class="time-row time-total">
            <span class="time-who">everyone</span>
            <span class="time-hours">{{ formatHours(spentHours) }}</span>
          </div>
        </div>
      </template>

      <template v-if="durationHours !== null">
        <label>scheduled</label>
        <span style="color: var(--muted)">
          {{ durationHours }}h
          <template v-if="task.estHours"> · estimated {{ task.estHours }}h</template>
          <template v-if="spentHours > 0"> · spent {{ formatHours(spentHours) }}</template>
        </span>
      </template>

      <label>who</label>
      <div style="display: flex; gap: 4px; flex-wrap: wrap">
        <button
          v-for="u in assignable"
          :key="u.id"
          class="chip"
          :style="{
            color: task.assignees.includes(u.id) ? '#fff' : 'var(--muted)',
            background: task.assignees.includes(u.id) ? u.color : 'transparent',
            borderColor: u.color,
          }"
          @click="toggleAssignee(u.id)"
        >{{ u.displayName }}</button>
      </div>

      <label>notes <LockTag v-for="u in editors('notes')" :key="u.id" :user="u" /></label>
      <div class="collab-wrap">
        <textarea
          ref="notesInput"
          v-collab="{ key: task.id, field: 'notes', value: task.notes, live: !unsaved }"
          v-bind="lockable('notes')"
          rows="3"
          @change="unsaved && setField('notes', ($event.target as HTMLTextAreaElement).value)"
        ></textarea>
        <RemoteCarets :target="notesInput" :sels="sels('notes')" />
      </div>
    </div>

    <!-- Always shown, even with no children: it is where the tree is edited
         and navigated from, not just a list of what is underneath. An unsaved
         task is the exception — nothing can hang under it yet. -->
    <section v-if="!unsaved" class="subtasks">
      <div class="subtasks-head">
        <span>sub-tasks</span>
        <span v-if="children.length" class="subtasks-tally">{{ childrenDone }}/{{ children.length }} done</span>
        <label v-if="childrenDone" class="subtasks-hide">
          <input type="checkbox" v-model="viewport.hideDoneSubtasks" />
          hide done
        </label>
        <span class="subtasks-actions">
          <button
            v-if="parent"
            class="parent-link"
            type="button"
            :title="`Open parent: ${parent.title}`"
            @click="board.select(parent.id)"
          >↑ {{ parent.title }}</button>
          <button class="btn ghost" type="button" @click="addSibling">+ Sibling</button>
          <button class="btn ghost" type="button" @click="addChild">+ Child</button>
        </span>
      </div>
      <div v-if="shownChildren.length" class="subtasks-list">
        <div
          v-for="c in shownChildren"
          :key="c.id"
          class="subtask"
          :class="{ done: c.status === 'done' }"
          :title="`Open “${c.title}”`"
          @click="board.select(c.id)"
        >
          <!-- Same rule as the tree: the rolled-up progress of what is under a
               sub-task, and beside it the chip that says whether the sub-task
               itself is finished. Nothing but that chip ever finishes it. -->
          <span v-if="childCount(c.id)" class="pct">{{ childPct(c.id) }}%</span>
          <StatusChip
            :status="c.status"
            @change="(s: Status) => board.setStatus(c.id, s)"
          />
          <span class="subtask-title">{{ c.title }}</span>
          <span
            v-for="id in c.assignees"
            :key="id"
            class="avatar"
            :style="{ background: board.userColor(id) }"
            :title="board.userName(id)"
          >{{ board.userName(id).slice(0, 2) }}</span>
          <span v-if="childCount(c.id)" class="subtask-count" title="sub-tasks">{{ childCount(c.id) }} ›</span>
        </div>
      </div>
    </section>

    <div v-if="!unsaved" class="timeline-head">
      <label>
        <input type="checkbox" v-model="viewport.commentsOnly" />
        comments only
      </label>
      <span v-if="viewport.commentsOnly && hiddenCount" class="timeline-hidden">
        {{ hiddenCount }} hidden
      </span>
    </div>

    <div v-if="!unsaved" class="timeline">
      <TimelineEntry v-for="e in entries" :key="e.id" :entry="e" />
      <p v-if="entries.length === 0" style="color: var(--faint)">
        {{ viewport.commentsOnly && hiddenCount ? "No comments yet." : "No activity yet." }}
      </p>
    </div>

    <!-- Nothing to comment on, and nowhere to put it, until it is saved. -->
    <div v-if="!unsaved" class="composer">
      <textarea
        v-model="draft"
        placeholder="Comment… (⌘/Ctrl+Enter to send)"
        @keydown.enter.meta.prevent="send"
        @keydown.enter.ctrl.prevent="send"
      ></textarea>
      <button class="btn primary" :disabled="!draft.trim()" @click="send">Send</button>
    </div>

    <MoveToTeamModal v-if="moveTeamOpen" :key="task.id" :task="task" @close="moveTeamOpen = false" />
  </aside>
</template>
